import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CARS_BOOKED_STATE_ASSIGNED,
  CARS_BOOKED_STATE_NOT_ASSIGNED,
} from 'lib/carsBookedStates'
import { addDays, isAfter, isBefore, parse, subDays } from 'date-fns'
import getYearMonthDayDate from 'lib/getYearMonthDayDate'
import {
  CAR_CHOICE_ACCEPTED,
  CAR_CHOICE_ACCEPTED_WITH_REPRICE,
} from 'lib/carChoicesLib'

import {
  checkToBeAssigned,
  checkRecapChoice,
  checkNotAssigned,
  checkAssigned,
} from 'components/organisms/Assignment/assignmentLib'

export const FILTER_CHOICES_NONE_CHOICE = ''
export const FILTER_CHOICES_ACCEPTED_CHOICE = CAR_CHOICE_ACCEPTED
export const FILTER_CHOICES_ACCEPTED_WITH_REPRICE_CHOICE =
  CAR_CHOICE_ACCEPTED_WITH_REPRICE
export const FILTER_CHOICES_ALL_CHOICES = 'allChoices'
export const FILTER_CHOICES_NO_CHOICES = 'noChoices'

export const FILTER_ASSIGNMENT_CHOICES_NONE_CHOICE = ''
export const FILTER_ASSIGNMENT_CHOICES_ACCEPTED_CHOICE = CAR_CHOICE_ACCEPTED
export const FILTER_ASSIGNMENT_CHOICES_ACCEPTED_WITH_REPRICE_CHOICE =
  CAR_CHOICE_ACCEPTED_WITH_REPRICE
export const FILTER_ASSIGNMENT_CHOICES_NOT_CHOSEN = 'notChosen'

export const FILTER_ASSIGNMENT_TYPE_NONE_CHOICE = ''
export const FILTER_ASSIGNMENT_TYPE_TO_BE_ASSIGNED_CHOICE = 'toBeAssigned'
export const FILTER_ASSIGNMENT_TYPE_ASSIGNED_CHOICE = 'assigned'
export const FILTER_ASSIGNMENT_TYPE_NOT_ASSIGNED_CHOICE = 'notAssigned'

export const FILTER_CARS_BOOKED_TYPE_NONE_CHOICE = ''
export const FILTER_CARS_BOOKED_TYPE_ACCEPTED_CHOICE = CAR_CHOICE_ACCEPTED
export const FILTER_CARS_BOOKED_TYPE_ACCEPTED_WITH_REPRICE =
  CAR_CHOICE_ACCEPTED_WITH_REPRICE
export const FILTER_CARS_BOOKED_TYPE_ASSIGNED_CHOICE =
  CARS_BOOKED_STATE_ASSIGNED
export const FILTER_CARS_BOOKED_TYPE_NOT_ASSIGNED_CHOICE =
  CARS_BOOKED_STATE_NOT_ASSIGNED

const DATE_FORMAT = 'yyyy-MM-dd'

function filterKm(car, filters) {
  return !(car.km < filters.kmMin || car.km > filters.kmMax)
}

function filterDateRange(date, from, to) {
  const formattedFrom = subDays(
    parse(
      from,
      // t('common.date.format.formatDMYISO'),
      DATE_FORMAT,
      new Date()
    ),
    1
  )
  const formattedTo = addDays(
    parse(
      to,
      // t('common.date.format.formatDMYISO'),
      DATE_FORMAT,
      new Date()
    ),
    1
  )

  const formattedDate = date
    ? parse(getYearMonthDayDate(new Date(date)), DATE_FORMAT, new Date())
    : null

  const fromCheck =
    from !== null ? isBefore(formattedFrom, formattedDate) : true
  const toCheck = to !== null ? isAfter(formattedTo, formattedDate) : true

  return fromCheck && toCheck
}

function filterYear(car, filters) {
  return !(
    (filters.yearMin !== '' && car.year < filters.yearMin) ||
    (filters.yearMax !== '' && car.year > filters.yearMax)
  )
}

function filterQuery(car, filters) {
  const tokens =
    filters.query !== '' ? filters.query.trim().split(/[\s,]+/) : []

  return !(
    tokens.length &&
    (car?.title
      ? !tokens.every((token) => car.title.match(new RegExp(token, 'i')))
      : true) &&
    !tokens.some((token) => car.plate.match(new RegExp(token, 'i'))) &&
    !tokens.some((token) => car.vin.match(new RegExp(token, 'i')))
  )
}

function filterPlateQuery(car, filters) {
  return !(
    filters.plateQuery !== '' &&
    !car.plate.match(new RegExp(filters.plateQuery.trim(), 'i'))
  )
}

function filterVinQuery(car, filters) {
  return !(
    filters.vinQuery !== '' &&
    !car.vin.match(new RegExp(filters.vinQuery.trim(), 'i'))
  )
}

function firstBlockFilter(
  car,
  filters,
  t,
  {
    ALL_OPTION,
    EXISTS_PRICE_OK,
    EXISTS_PRICE_MISSING,
    EXISTS_OK,
    EXISTS_MISSING,
  }
) {
  if (!filterKm(car, filters)) return false
  if (
    !filterDateRange(
      car?.storagePlaceDate,
      filters?.storagePlaceDateFrom,
      filters?.storagePlaceDateTo
    )
  )
    return false
  if (!filterYear(car, filters)) return false
  if (!filterQuery(car, filters)) return false
  if (!filterPlateQuery(car, filters)) return false
  if (!filterVinQuery(car, filters)) return false
  return true
}

function secondBlockFilter(car, filters, t, { ALL_OPTION }) {
  if (
    [car.soldPartnerName, ALL_OPTION].every(
      (partner) => !filters.partners.includes(partner)
    )
  )
    return false

  if (
    [car.storagePlace, ALL_OPTION].every(
      (storagePlace) => !filters.storagePlaces.includes(storagePlace)
    )
  )
    return false

  if ([car.brand, ALL_OPTION].every((brand) => !filters.brands.includes(brand)))
    return false

  if ([car.model, ALL_OPTION].every((model) => !filters.models.includes(model)))
    return false

  if ([car.fuelType, ALL_OPTION].every((fuel) => !filters.fuels.includes(fuel)))
    return false

  return true
}

function thirdBlockFilter(car, filters, t, { ALL_OPTION }) {
  if (
    filters.choice &&
    (filters.choice === FILTER_CHOICES_ALL_CHOICES
      ? car.choice !== FILTER_CHOICES_ACCEPTED_CHOICE &&
        car.choice !== FILTER_CHOICES_ACCEPTED_WITH_REPRICE_CHOICE
      : filters.choice === FILTER_CHOICES_NO_CHOICES
      ? car.choice !== null
      : car.choice !== filters.choice)
  )
    return false

  if (
    filters.assignmentChoice &&
    !checkRecapChoice(
      car,
      filters.assignmentPartners,
      filters.assignmentChoice !== FILTER_ASSIGNMENT_CHOICES_NOT_CHOSEN
        ? filters.assignmentChoice
        : undefined
    )
  )
    return false

  if (
    filters.assignmentType &&
    !(filters.assignmentType === FILTER_ASSIGNMENT_TYPE_NOT_ASSIGNED_CHOICE
      ? checkNotAssigned(car, filters.assignmentPartners)
      : filters.assignmentType === FILTER_ASSIGNMENT_TYPE_ASSIGNED_CHOICE
      ? checkAssigned(car, filters.assignmentPartners)
      : checkToBeAssigned(car, filters.assignmentPartners))
  )
    return false

  if (!filterDateRange(car?.date, filters?.dateFrom, filters?.dateTo))
    return false

  return true
}

const useCarsFilters = ({ startFilters, startDataFilters } = {}) => {
  const { t } = useTranslation()

  const ALL_OPTION = t('common.form.all')
  const EXISTS_MISSING = t('common.filters.carFoundMissing')
  const EXISTS_OK = t('common.filters.carFoundOk')
  const EXISTS_PRICE_MISSING = t('common.filters.priceMissing')
  const EXISTS_PRICE_OK = t('common.filters.priceOk')

  const defaultFilters = useMemo(
    () => ({
      kmMin: 0,
      kmMax: 0,
      yearMin: '',
      yearMax: '',
      storagePlaceDateFrom: null,
      storagePlaceDateTo: null,
      dateFrom: null,
      dateTo: null,
      query: '',
      plateQuery: '',
      vinQuery: '',
      choice: '',
      assignmentChoice: '',
      assignmentType: '',
      carsBookedType: '',
      exists: ALL_OPTION,
      assignmentPartners: '',
      partners: [ALL_OPTION],
      storagePlaces: [ALL_OPTION],
      brands: [ALL_OPTION],
      models: [ALL_OPTION],
      fuels: [ALL_OPTION],
      ...startFilters,
    }),
    [ALL_OPTION, startFilters]
  )

  const [baseFilters, setBaseFilters] = useState({ ...defaultFilters })

  const [filters, setFilters] = useState({
    ...defaultFilters,
    initialized: false,
  })

  const [filtersData, setFiltersData] = useState({
    kmMin: 0,
    kmMax: 0,
    yearMin: 0,
    yearMax: 0,
    assignmentPartners: [''],
    partners: [ALL_OPTION],
    storagePlaces: [ALL_OPTION],
    brands: [ALL_OPTION],
    models: [ALL_OPTION],
    fuels: [ALL_OPTION],
    ...startDataFilters,
  })

  const createAttributeList = useCallback(
    (list, attributeKey, mapFunction, acceptEmpty = false) => {
      return [
        ALL_OPTION,
        ...list
          .reduce(
            (previous, currentItem) =>
              (!acceptEmpty && !currentItem[attributeKey]) ||
              previous.includes(
                mapFunction
                  ? mapFunction(currentItem[attributeKey])
                  : currentItem[attributeKey]
              )
                ? previous
                : [
                    ...previous,
                    mapFunction
                      ? mapFunction(currentItem[attributeKey])
                      : currentItem[attributeKey],
                  ],
            []
          )
          .sort(),
      ]
    },
    [ALL_OPTION]
  )

  const initFilters = useCallback(
    (cars) => {
      const kms = cars.map((car) => car.km)
      const kmMin = Math.floor(Math.min(...kms) / 100) * 100
      const kmMax = Math.ceil(Math.max(...kms) / 100) * 100

      const years = cars.map((car) => car.year)

      const storagePlaces = createAttributeList(cars, 'storagePlace')

      const brands = createAttributeList(cars, 'brand')

      const models = createAttributeList(cars, 'model')

      const fuels = createAttributeList(cars, 'fuelType')

      const partners = createAttributeList(cars, 'soldPartnerName')

      setBaseFilters((s) => ({
        ...s,
        kmMin,
        kmMax,
      }))
      setFilters((s) => ({
        ...s,
        kmMin,
        kmMax,
        initialized: true,
      }))

      setFiltersData((s) => ({
        ...s,
        kmMin,
        kmMax,
        yearMin: Math.min(...years),
        yearMax: Math.max(...years),
        partners,
        storagePlaces,
        brands,
        models,
        fuels,
      }))
    },
    [createAttributeList]
  )

  const resetFilters = useCallback(() => {
    setFilters((s) => ({ ...s, ...baseFilters }))
  }, [baseFilters])

  const setKmRange = useCallback(
    (kmMin, kmMax) => {
      setFilters((s) => ({ ...s, kmMin, kmMax }))
    },
    [setFilters]
  )

  const setYearRange = useCallback(
    (yearMin, yearMax) => {
      setFilters((s) => ({ ...s, yearMin, yearMax }))
    },
    [setFilters]
  )

  const setStoragePlaceDateRange = useCallback(
    (storagePlaceDateFrom, storagePlaceDateTo) => {
      setFilters((s) => ({
        ...s,
        storagePlaceDateFrom,
        storagePlaceDateTo,
      }))
    },
    [setFilters]
  )

  const setDateRange = useCallback(
    (dateFrom, dateTo) => {
      setFilters((s) => ({
        ...s,
        dateFrom,
        dateTo,
      }))
    },
    [setFilters]
  )

  const setPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partners: value }))
    },
    [setFilters]
  )

  const setAssignmentPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, assignmentPartners: value }))
    },
    [setFilters]
  )

  const setStoragePlaces = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, storagePlaces: value }))
    },
    [setFilters]
  )

  const setBrands = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, brands: value }))
    },
    [setFilters]
  )

  const setModels = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, models: value }))
    },
    [setFilters]
  )

  const setFuels = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, fuels: value }))
    },
    [setFilters]
  )

  const setQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, query: value }))
    },
    [setFilters]
  )

  const setPlateQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, plateQuery: value }))
    },
    [setFilters]
  )

  const setVinQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, vinQuery: value }))
    },
    [setFilters]
  )

  const setChoice = useCallback(
    (value) => {
      if (value === filters.choice) {
        return
      }
      setFilters((s) => ({
        ...s,
        choice: value,
      }))
    },
    [filters.choice]
  )

  const setAssignmentChoice = useCallback(
    (value) => {
      if (value === filters.assignmentChoice) {
        return
      }
      setFilters((s) => ({
        ...s,
        assignmentChoice: value,
      }))
    },
    [filters.assignmentChoice]
  )

  const setAssignmentType = useCallback(
    (value) => {
      if (value === filters.assignmentType) {
        return
      }
      setFilters((s) => ({
        ...s,
        assignmentType: value,
      }))
    },
    [filters.assignmentType]
  )

  const setCarsBookedType = useCallback(
    (value) => {
      if (value === filters.carsBookedType) {
        return
      }
      setFilters((s) => ({
        ...s,
        carsBookedType: value,
      }))
    },
    [filters.carsBookedType]
  )

  const filterCars = useCallback(
    (cars, filters) => {
      return cars.filter((car) => {
        if (
          !firstBlockFilter(car, filters, t, {
            ALL_OPTION,
            EXISTS_PRICE_OK,
            EXISTS_PRICE_MISSING,
            EXISTS_OK,
            EXISTS_MISSING,
          })
        )
          return false

        if (
          !secondBlockFilter(car, filters, t, {
            ALL_OPTION,
          })
        )
          return false

        if (
          !thirdBlockFilter(car, filters, t, {
            ALL_OPTION,
          })
        )
          return false

        if (
          filters.carsBookedType &&
          !([
            FILTER_CARS_BOOKED_TYPE_ACCEPTED_CHOICE,
            FILTER_CARS_BOOKED_TYPE_ACCEPTED_WITH_REPRICE,
          ].includes(filters.carsBookedType)
            ? car?.choice === filters.carsBookedType
            : car?.assignedState === filters.carsBookedType)
        )
          return false

        return true
      })
    },
    [
      ALL_OPTION,
      EXISTS_MISSING,
      EXISTS_OK,
      EXISTS_PRICE_MISSING,
      EXISTS_PRICE_OK,
      t,
    ]
  )

  return {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setYearRange,
    setStoragePlaceDateRange,
    setDateRange,
    setPartners,
    setAssignmentPartners,
    setStoragePlaces,
    setBrands,
    setModels,
    setFuels,
    setQuery,
    setPlateQuery,
    setVinQuery,
    setChoice,
    setAssignmentChoice,
    setAssignmentType,
    setCarsBookedType,
    filterCars,
    resetFilters,
  }
}

export default useCarsFilters
